import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import { Dossier, DossierStateHistory } from '@sc/data-access-models';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { MomentParseZonePipe } from '@sc/ui-components';
import { isString } from 'lodash-es';
import { DeclarationEndHistoryAppointmentComponent } from './declaration-end-history-appointment/declaration-end-history-appointment.component';
import { DeclarationEndHistoryDateComponent } from './declaration-end-history-date/declaration-end-history-date.component';

@Component({
  selector: 'sc-declaration-end-history',
  styleUrls: ['./declaration-end-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    DeclarationEndHistoryDateComponent,
    NgFor,
    DeclarationEndHistoryAppointmentComponent,
    NgTemplateOutlet,
    AsyncPipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <section>
      <h2 class="sc-design-title-h1">
        {{ 'DeclarationEndHistory.title' | translate }}
      </h2>

      <ng-template #cancelBloc let-state="state">
        <ng-container *ngIf="state?.state === 'CANCELLED'">
          <sc-declaration-end-history-date
            [state]="state"
            class="cancelled"
            icon="close"
          ></sc-declaration-end-history-date>

          <aside>
            <div
              [innerHTML]="
                'DeclarationEndHistory.cancel.' + state.userType | translate
              "
            ></div>
          </aside>
        </ng-container>
      </ng-template>

      <div *ngFor="let state of filteredStates; let idx = index">
        <ng-container *ngIf="state.state === 'COMPLETE'">
          <sc-declaration-end-history-date
            [state]="state"
          ></sc-declaration-end-history-date>

          <ng-container *ngIf="isRequestCreationState(state)">
            <aside>
              <div>
                {{ 'DeclarationEndHistory.book.oldRequest' | translate }}
              </div>
            </aside>
          </ng-container>

          <ng-container *ngIf="isFirstBook(state, idx)">
            <aside>
              <div>{{ 'DeclarationEndHistory.book.first' | translate }}</div>
              <sc-declaration-end-history-appointment
                [appointment]="getAppointment(state?.data?.appointmentId)"
              ></sc-declaration-end-history-appointment>
            </aside>
          </ng-container>

          <ng-container *ngIf="isNotLastBook(state, idx)">
            <aside>
              <div>{{ displayTitleBook(state, idx) | translate }}</div>
              <sc-declaration-end-history-appointment
                [appointment]="getAppointment(state?.data?.appointmentId)"
              ></sc-declaration-end-history-appointment>
            </aside>
          </ng-container>

          <ng-container *ngIf="isLastBook(state, idx)">
            <aside>
              <div>{{ displayTitleBook(state, idx) | translate }}</div>
            </aside>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="state.state === 'SLOT_SUGGESTED'">
          <ng-container *ngIf="isLastState(idx)">
            <sc-declaration-end-history-date
              [state]="state"
              class="slotSuggested"
              icon="time_later"
            ></sc-declaration-end-history-date>

            <aside>
              <div
                [innerHTML]="
                  'DeclarationEndHistory.modification.waiting' | translate
                "
              ></div>
            </aside>
          </ng-container>

          <sc-declaration-end-history-date
            [state]="state"
          ></sc-declaration-end-history-date>

          <aside>
            <div
              [innerHTML]="
                'DeclarationEndHistory.modification.' + state.userType
                  | translate
              "
            ></div>

            <ng-container
              *ngIf="isLastState(idx) && state.data?.suggestedAppointments"
            >
              <br />
              <div
                [innerHTML]="
                  'DeclarationEndHistory.modification.suggestions' | translate
                "
              ></div>

              <div *ngFor="let suggestion of state.data?.suggestedAppointments">
                {{
                  'DeclarationEndHistory.interventionDate'
                    | translate
                      : {
                          date:
                            suggestion?.time
                            | parseZoneTimeString: suggestion?.timezone
                            | localeDate: 'longDate'
                            | async,
                          hour:
                            suggestion?.time
                            | parseZoneTimeString: suggestion?.timezone
                            | localeDate: 'shortTime'
                            | async
                        }
                }}
              </div>
            </ng-container>
          </aside>
        </ng-container>

        <ng-container
          *ngTemplateOutlet="cancelBloc; context: { state: state }"
        ></ng-container>
      </div>

      <ng-container *ngIf="hasRequestedAppointment()">
        <div *ngIf="this.dossier.requestAppointment.isValidatedByFitter">
          <aside>
            <div>{{ 'DeclarationEndHistory.book.processed' | translate }}</div>
            <sc-declaration-end-history-appointment
              [appointment]="dossier?.requestAppointment"
              [updateTime]="this.getDateProcessed()"
            ></sc-declaration-end-history-appointment>
          </aside>
        </div>

        <div>
          <aside>
            <div>{{ 'DeclarationEndHistory.book.request' | translate }}</div>
            <sc-declaration-end-history-appointment
              [appointment]="dossier?.requestAppointment"
              [updateTime]="this.getDateSent()"
            ></sc-declaration-end-history-appointment>
          </aside>
        </div>

        <ng-container
          *ngTemplateOutlet="
            cancelBloc;
            context: { state: getLastRequestCancelState() }
          "
        ></ng-container>
      </ng-container>

      <ng-container
        *ngIf="
          dossier?.state === 'ABANDONED' && dossier?.searchCriteria?.address
        "
      >
        <aside class="search_address_container">
          <div>
            {{ 'DeclarationEndHistory.searchedAddressTitle' | translate }}
          </div>
          <div class="search_address_detail">
            {{ dossier?.searchCriteria?.address?.addressElements.streetNumber }}
            {{ dossier?.searchCriteria?.address?.addressElements.streetName }}
            {{ dossier?.searchCriteria?.address?.addressElements?.city }}
            {{ dossier?.searchCriteria?.address?.addressElements?.postalCode }}
          </div>
        </aside>
      </ng-container>
    </section>
  `,
})
export class DeclarationEndHistoryComponent {
  @Input() set dossier(dossier: Dossier) {
    this._dossier = dossier;
    this.filteredStates = this.statesToDisplay();
  }
  get dossier(): Dossier {
    return this._dossier;
  }
  private _dossier: Dossier;
  filteredStates: DossierStateHistory[];

  constructor(public timezoneService: TimezoneService) {}

  statesToDisplay() {
    return (
      this.dossier?.stateHistory
        .filter(
          (state) =>
            state.state === 'COMPLETE' ||
            (state.state === 'CANCELLED' &&
              (!!state?.data?.appointment?.status ||
                !!state?.data?.requestAppointment?.status)) ||
            (state.state === 'SLOT_SUGGESTED' && state.data)
        )
        .sort((state1, state2) => {
          return (state2?.timestamp as unknown as string).localeCompare(
            state1?.timestamp as unknown as string
          );
        }) ?? []
    );
  }

  isFirstBook(state: DossierStateHistory, idx: number) {
    return (
      state.state === 'COMPLETE' &&
      idx === this.filteredStates.length - 1 &&
      !!this.dossier?.appointment
    );
  }

  isNotLastBook(state: DossierStateHistory, idx: number) {
    return (
      !this.isFirstBook(state, idx) && state.state === 'COMPLETE' && idx !== 0
    );
  }

  isLastBook(state: DossierStateHistory, idx: number) {
    return (
      !this.isFirstBook(state, idx) && state.state === 'COMPLETE' && idx === 0
    );
  }

  isLastState(idx: number) {
    return idx === this.filteredStates?.length - 1;
  }

  isRequestCreationState(state: DossierStateHistory) {
    return (
      !!state.data?.requestAppointment?.fitterId?.__new ||
      !!state.data?.requestAppointment?.fitterId__added ||
      isString(state.data?.requestAppointment?.fitterId)
    );
  }

  getLastRequestCancelState() {
    return this.filteredStates
      ?.filter(
        (state) => state.state === 'CANCELLED' && state.data?.requestAppointment
      )
      .reverse()[0];
  }

  getAppointment(appointmentId: string) {
    if (!appointmentId || this.dossier?.appointment?.id === appointmentId) {
      return this.dossier?.appointment;
    } else {
      return this.dossier?.previousAppointments?.find(
        (apt) => apt.id === appointmentId
      );
    }
  }

  hasRequestedAppointment() {
    return !!this.dossier?.requestAppointment && !this.dossier?.appointment;
  }

  getDateProcessed() {
    return this.dossier.stateHistory.find((stateHistory) => {
      if (
        stateHistory.data?.requestAppointment?.isValidatedByFitter?.__new ==
          true ||
        stateHistory.data?.requestAppointment?.isValidatedByFitter__added ==
          true
      ) {
        return true;
      } else {
        return false;
      }
    })?.timestamp;
  }

  getDateSent() {
    return this.dossier.stateHistory.find((stateHistory) => {
      if (stateHistory.data?.requestAppointment) {
        return true;
      } else {
        return false;
      }
    })?.timestamp;
  }

  displayTitleBook(state: DossierStateHistory, idx: number) {
    const hasAppointmentDetails =
      state.data.appointment?.time ||
      state.data.appointment?.endTime ||
      state.data.appointment?.interventionLocation ||
      state.data.appointment?.assignedReplacementVehicle ||
      state.data.appointment?.fitterId;

    if (idx === 0) {
      return hasAppointmentDetails
        ? 'DeclarationEndHistory.book.last'
        : 'DeclarationEndHistory.book.update';
    } else {
      return hasAppointmentDetails
        ? 'DeclarationEndHistory.book.notLast'
        : 'DeclarationEndHistory.book.update';
    }
  }
}
